import type { SourceInteractionRelationSetV1 } from "./types"

export const PLATFORM_LIBRARY_KNOWLEDGE_BUNDLE_PATH =
  "/api/v3/kg/library-knowledge-bundle" as const
export const PLATFORM_LIBRARY_RAG_PLAN_PATH =
  "/api/v3/kg/library-rag/plan" as const
export const PLATFORM_LIBRARY_RAG_CRITIC_PATH =
  "/api/v3/kg/library-rag/critic" as const

export type PlatformLibraryLocale =
  | "en-US"
  | "ko-KR"
  | "ja-JP"
  | "zh-CN"
  | "zh-TW"

export interface PlatformLibraryRagRequestV1 {
  prompt: string
  target: "web"
  locale: PlatformLibraryLocale
  applicationInstanceId: string
}

export interface PlatformLibraryKnowledgeAssetV1 {
  ref: string
  schemaVersion: string
  sourceRef: string
  contentHash: `sha256:${string}`
}

export interface PlatformApplicationBlueprintAssetV1
  extends PlatformLibraryKnowledgeAssetV1 {
  applicationBlueprintId: string
  applicationCoreRef: string
  version: string
  target: "web"
  locale: PlatformLibraryLocale
  userGoal: string
  uxPatternRefs: string[]
}

export interface PlatformUxPatternAssetV1
  extends PlatformLibraryKnowledgeAssetV1 {
  uxPatternId: string
  version: string
  target: "web"
  intent: string
}

export interface PlatformDesignOntologyAssetV1
  extends PlatformLibraryKnowledgeAssetV1 {
  applicationRef: string
  releaseRef: string
}

export interface PlatformLibraryKnowledgeBundleV1 {
  schemaVersion: "cogo.library-knowledge-bundle.v1"
  canonicalJsonVersion: "cogo.canonical-json.v1"
  request: PlatformLibraryRagRequestV1
  requestHash: `sha256:${string}`
  applicationBlueprint: PlatformApplicationBlueprintAssetV1
  uxPatterns: PlatformUxPatternAssetV1[]
  designOntology: PlatformDesignOntologyAssetV1
  sourceInteractionRelationSet: SourceInteractionRelationSetV1
  provenance: {
    authority: "cogo-kg-worker-embedded"
    sourceRefs: string[]
    assetContentHashes: Array<`sha256:${string}`>
  }
  contentHash: `sha256:${string}`
}

export interface PlatformLibraryRagSelectionV1 {
  schemaVersion: "cogo.library-rag-selection.v1"
  normalizedIntent: string
  applicationBlueprintRef: string
  uxPatternRefs: string[]
  designOntologyReleaseRef: string
  reasons: string[]
  traceId: string
}

export interface PlatformLibraryAgentReceiptV1 {
  role: "knowledge-planner" | "semantic-critic"
  provider: "vllm"
  requestedModel: string
  servedModelId: string
  reviewRunId: string
  sourceHashes: Record<string, `sha256:${string}`>
  promptTemplateHash: `sha256:${string}`
  inputHash: `sha256:${string}`
  outputHash: `sha256:${string}`
  status: "pass"
  reasonCodes: string[]
  selectedCandidateIds: string[]
}

export interface PlatformLibraryRagPlanReceiptV1 {
  schemaVersion: "cogo.library-rag-plan-receipt.v1"
  request: Omit<PlatformLibraryRagRequestV1, "prompt">
  requestHash: `sha256:${string}`
  scopeHash: `sha256:${string}`
  bundleContentHash: `sha256:${string}`
  selection: PlatformLibraryRagSelectionV1
  planner: {
    identity: {
      role: "knowledge-planner"
      provider: "vllm"
      model: string
    }
    receipt: PlatformLibraryAgentReceiptV1
  }
  contentHash: `sha256:${string}`
  signature: {
    algorithm: "hmac-sha256"
    keyId: string
    value: string
  }
}

export interface PlatformLibrarySemanticCriticVerdictV1 {
  schemaVersion: "cogo.library-semantic-critic-verdict.v1"
  verdict: "pass"
  findings: []
  traceId: string
}

export interface PlatformLibraryRagCriticReceiptV1 {
  schemaVersion: "cogo.library-rag-critic-receipt.v1"
  request: Omit<PlatformLibraryRagRequestV1, "prompt">
  requestHash: `sha256:${string}`
  scopeHash: `sha256:${string}`
  bundleContentHash: `sha256:${string}`
  planReceiptContentHash: `sha256:${string}`
  compiledArtifactContentHash: `sha256:${string}`
  selection: PlatformLibraryRagSelectionV1
  critic: {
    identity: {
      role: "semantic-critic"
      provider: "vllm"
      model: string
    }
    verdict: PlatformLibrarySemanticCriticVerdictV1
    receipt: PlatformLibraryAgentReceiptV1
  }
  contentHash: `sha256:${string}`
  signature: {
    algorithm: "hmac-sha256"
    keyId: string
    value: string
  }
}

export async function hashPlatformCanonicalJson(
  value: unknown,
): Promise<`sha256:${string}`> {
  const canonical = canonicalizePlatformJson(value, new Set<object>())
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical),
  )
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`
}

export async function parsePlatformLibraryKnowledgeBundleV1(
  value: unknown,
): Promise<PlatformLibraryKnowledgeBundleV1> {
  if (!isPlatformLibraryKnowledgeBundleV1(value)) {
    throw new Error("platform-library-knowledge-bundle-invalid")
  }
  if ((await hashPlatformCanonicalJson(value.request)) !== value.requestHash) {
    throw new Error("platform-library-knowledge-request-hash-mismatch")
  }
  const { contentHash, ...unsigned } = value
  if ((await hashPlatformCanonicalJson(unsigned)) !== contentHash) {
    throw new Error("platform-library-knowledge-bundle-hash-mismatch")
  }
  const { contentHash: relationHash, ...relationBody } =
    value.sourceInteractionRelationSet
  if ((await hashPlatformCanonicalJson(relationBody)) !== relationHash) {
    throw new Error("platform-library-interaction-hash-mismatch")
  }
  return structuredClone(value)
}

export async function parsePlatformLibraryRagPlanReceiptV1(
  value: unknown,
): Promise<PlatformLibraryRagPlanReceiptV1> {
  if (!isPlanReceipt(value)) throw new Error("platform-library-plan-invalid")
  const { contentHash, signature: _signature, ...unsigned } = value
  if ((await hashPlatformCanonicalJson(unsigned)) !== contentHash) {
    throw new Error("platform-library-plan-hash-mismatch")
  }
  return structuredClone(value)
}

export async function parsePlatformLibraryRagCriticReceiptV1(
  value: unknown,
): Promise<PlatformLibraryRagCriticReceiptV1> {
  if (!isCriticReceipt(value)) {
    throw new Error("platform-library-critic-invalid")
  }
  const { contentHash, signature: _signature, ...unsigned } = value
  if ((await hashPlatformCanonicalJson(unsigned)) !== contentHash) {
    throw new Error("platform-library-critic-hash-mismatch")
  }
  return structuredClone(value)
}

function canonicalizePlatformJson(value: unknown, ancestors: Set<object>): string {
  if (value === null) return "null"
  if (typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value)
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError("non-finite-json-number")
    return JSON.stringify(value)
  }
  if (typeof value !== "object") throw new TypeError("non-json-value")
  if (ancestors.has(value)) throw new TypeError("cyclic-json-value")
  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      return `[${value
        .map((item) => canonicalizePlatformJson(item, ancestors))
        .join(",")}]`
    }
    if (Object.getPrototypeOf(value) !== Object.prototype) {
      throw new TypeError("non-plain-json-object")
    }
    const descriptors = Object.getOwnPropertyDescriptors(value)
    const entries = Object.keys(descriptors)
      .sort()
      .flatMap((key) => {
        const descriptor = descriptors[key]
        if (!descriptor.enumerable) return []
        if (!("value" in descriptor)) throw new TypeError("json-accessor")
        return [
          `${JSON.stringify(key)}:${canonicalizePlatformJson(
            descriptor.value,
            ancestors,
          )}`,
        ]
      })
    return `{${entries.join(",")}}`
  } finally {
    ancestors.delete(value)
  }
}

function isPlatformLibraryKnowledgeBundleV1(
  value: unknown,
): value is PlatformLibraryKnowledgeBundleV1 {
  if (
    !hasExactKeys(value, [
      "schemaVersion",
      "canonicalJsonVersion",
      "request",
      "requestHash",
      "applicationBlueprint",
      "uxPatterns",
      "designOntology",
      "sourceInteractionRelationSet",
      "provenance",
      "contentHash",
    ]) ||
    value.schemaVersion !== "cogo.library-knowledge-bundle.v1" ||
    value.canonicalJsonVersion !== "cogo.canonical-json.v1" ||
    !isRequest(value.request) ||
    !isSha256(value.requestHash) ||
    !isBlueprintAsset(value.applicationBlueprint) ||
    !Array.isArray(value.uxPatterns) ||
    value.uxPatterns.length === 0 ||
    value.uxPatterns.length > 32 ||
    !value.uxPatterns.every(isUxPatternAsset) ||
    !isDesignAsset(value.designOntology) ||
    !isRelationSet(value.sourceInteractionRelationSet) ||
    !isProvenance(value.provenance) ||
    !isSha256(value.contentHash)
  ) {
    return false
  }
  const requiredUxRefs = value.applicationBlueprint.uxPatternRefs
  const returnedUxRefs = value.uxPatterns.map((item) => item.ref)
  const relation = value.sourceInteractionRelationSet
  return (
    value.request.locale === value.applicationBlueprint.locale &&
    value.request.applicationInstanceId === relation.applicationInstanceId &&
    value.applicationBlueprint.ref === relation.applicationBlueprintRef &&
    value.applicationBlueprint.applicationCoreRef ===
      value.designOntology.applicationRef &&
    value.designOntology.releaseRef === relation.designOntologyReleaseRef &&
    sameOrderedValues(requiredUxRefs, returnedUxRefs) &&
    sameOrderedValues(requiredUxRefs, relation.uxPatternRefs) &&
    sameSet(
      [
        value.applicationBlueprint.contentHash,
        ...value.uxPatterns.map((item) => item.contentHash),
        value.designOntology.contentHash,
        relation.contentHash,
      ],
      value.provenance.assetContentHashes,
    )
  )
}

function isRequest(value: unknown): value is PlatformLibraryRagRequestV1 {
  return (
    hasExactKeys(value, ["prompt", "target", "locale", "applicationInstanceId"]) &&
    isBoundedText(value.prompt, 8_192) &&
    value.target === "web" &&
    isLocale(value.locale) &&
    isClosedIdentifier(value.applicationInstanceId)
  )
}

function isRequestSubject(
  value: unknown,
): value is Omit<PlatformLibraryRagRequestV1, "prompt"> {
  return (
    hasExactKeys(value, ["target", "locale", "applicationInstanceId"]) &&
    value.target === "web" &&
    isLocale(value.locale) &&
    isClosedIdentifier(value.applicationInstanceId)
  )
}

function isBaseAsset(value: unknown): value is PlatformLibraryKnowledgeAssetV1 {
  return (
    isRecord(value) &&
    isAuthorityRef(value.ref) &&
    isBoundedText(value.schemaVersion, 128) &&
    isSourceRef(value.sourceRef) &&
    isSha256(value.contentHash)
  )
}

function isBlueprintAsset(
  value: unknown,
): value is PlatformApplicationBlueprintAssetV1 {
  return (
    hasExactKeys(value, [
      "ref",
      "schemaVersion",
      "sourceRef",
      "contentHash",
      "applicationBlueprintId",
      "applicationCoreRef",
      "version",
      "target",
      "locale",
      "userGoal",
      "uxPatternRefs",
    ]) &&
    isBaseAsset(value) &&
    isClosedIdentifier(value.applicationBlueprintId) &&
    isAuthorityRef(value.applicationCoreRef) &&
    isVersion(value.version) &&
    value.ref === `${value.applicationBlueprintId}@${value.version}` &&
    value.target === "web" &&
    isLocale(value.locale) &&
    isBoundedText(value.userGoal, 4_096) &&
    isUniqueRefs(value.uxPatternRefs, 64)
  )
}

function isUxPatternAsset(value: unknown): value is PlatformUxPatternAssetV1 {
  return (
    hasExactKeys(value, [
      "ref",
      "schemaVersion",
      "sourceRef",
      "contentHash",
      "uxPatternId",
      "version",
      "target",
      "intent",
    ]) &&
    isBaseAsset(value) &&
    isClosedIdentifier(value.uxPatternId) &&
    isVersion(value.version) &&
    value.ref === `${value.uxPatternId}@${value.version}` &&
    value.target === "web" &&
    isBoundedText(value.intent, 4_096)
  )
}

function isDesignAsset(value: unknown): value is PlatformDesignOntologyAssetV1 {
  return (
    hasExactKeys(value, [
      "ref",
      "schemaVersion",
      "sourceRef",
      "contentHash",
      "applicationRef",
      "releaseRef",
    ]) &&
    isBaseAsset(value) &&
    isAuthorityRef(value.applicationRef) &&
    isAuthorityRef(value.releaseRef) &&
    value.ref === value.releaseRef
  )
}

function isRelationSet(value: unknown): value is SourceInteractionRelationSetV1 {
  return (
    hasExactKeys(value, [
      "schemaVersion",
      "relationSetId",
      "applicationBlueprintRef",
      "applicationInstanceId",
      "uxPatternRefs",
      "designOntologyReleaseRef",
      "designSourceRefs",
      "evidenceRefs",
      "relations",
      "contentHash",
    ]) &&
    value.schemaVersion === "cogo.source-interaction-relation-set.v1" &&
    isAuthorityRef(value.relationSetId) &&
    isAuthorityRef(value.applicationBlueprintRef) &&
    isClosedIdentifier(value.applicationInstanceId) &&
    isUniqueRefs(value.uxPatternRefs, 64) &&
    isAuthorityRef(value.designOntologyReleaseRef) &&
    isUniqueSourceRefs(value.designSourceRefs, 64) &&
    isUniqueRefs(value.evidenceRefs, 64) &&
    Array.isArray(value.relations) &&
    value.relations.length > 0 &&
    value.relations.length <= 128 &&
    value.relations.every(isRelation) &&
    isSha256(value.contentHash)
  )
}

function isRelation(value: unknown): boolean {
  return (
    hasExactKeys(value, [
      "blockId",
      "sourceNodeId",
      "elementId",
      "componentRef",
      "eventType",
      "flowId",
    ]) &&
    [
      value.blockId,
      value.sourceNodeId,
      value.elementId,
      value.componentRef,
      value.flowId,
    ].every(isAuthorityRef) &&
    ["tap", "change", "submit", "input", "select", "focus", "blur"].includes(
      String(value.eventType),
    )
  )
}

function isProvenance(value: unknown): value is PlatformLibraryKnowledgeBundleV1["provenance"] {
  return (
    hasExactKeys(value, ["authority", "sourceRefs", "assetContentHashes"]) &&
    value.authority === "cogo-kg-worker-embedded" &&
    isUniqueSourceRefs(value.sourceRefs, 128) &&
    Array.isArray(value.assetContentHashes) &&
    value.assetContentHashes.length > 0 &&
    value.assetContentHashes.length <= 128 &&
    value.assetContentHashes.every(isSha256) &&
    new Set(value.assetContentHashes).size === value.assetContentHashes.length
  )
}

function isSelection(value: unknown): value is PlatformLibraryRagSelectionV1 {
  return (
    hasExactKeys(value, [
      "schemaVersion",
      "normalizedIntent",
      "applicationBlueprintRef",
      "uxPatternRefs",
      "designOntologyReleaseRef",
      "reasons",
      "traceId",
    ]) &&
    value.schemaVersion === "cogo.library-rag-selection.v1" &&
    isBoundedText(value.normalizedIntent, 4_096) &&
    isAuthorityRef(value.applicationBlueprintRef) &&
    isUniqueRefs(value.uxPatternRefs, 64) &&
    isAuthorityRef(value.designOntologyReleaseRef) &&
    isTextArray(value.reasons, 16, 4_096) &&
    value.reasons.length > 0 &&
    isTrace(value.traceId)
  )
}

function isAgentReceipt(
  value: unknown,
  role: PlatformLibraryAgentReceiptV1["role"],
): value is PlatformLibraryAgentReceiptV1 {
  if (
    !hasExactKeys(value, [
      "role",
      "provider",
      "requestedModel",
      "servedModelId",
      "reviewRunId",
      "sourceHashes",
      "promptTemplateHash",
      "inputHash",
      "outputHash",
      "status",
      "reasonCodes",
      "selectedCandidateIds",
    ]) ||
    value.role !== role ||
    value.provider !== "vllm" ||
    !isBoundedText(value.requestedModel, 512) ||
    value.servedModelId !== value.requestedModel ||
    !isTrace(value.reviewRunId) ||
    !isRecord(value.sourceHashes) ||
    !Object.keys(value.sourceHashes).every(
      (key) => key.length > 0 && key.length <= 128,
    ) ||
    !Object.values(value.sourceHashes).every(isSha256) ||
    !isSha256(value.promptTemplateHash) ||
    !isSha256(value.inputHash) ||
    !isSha256(value.outputHash) ||
    value.status !== "pass" ||
    !isTextArray(value.reasonCodes, 16, 128) ||
    value.reasonCodes.length === 0 ||
    !isUniqueRefs(value.selectedCandidateIds, 64)
  ) {
    return false
  }
  return Object.keys(value.sourceHashes).length === (role === "knowledge-planner" ? 1 : 3)
}

function isPlanReceipt(value: unknown): value is PlatformLibraryRagPlanReceiptV1 {
  return (
    hasExactKeys(value, [
      "schemaVersion",
      "request",
      "requestHash",
      "scopeHash",
      "bundleContentHash",
      "selection",
      "planner",
      "contentHash",
      "signature",
    ]) &&
    value.schemaVersion === "cogo.library-rag-plan-receipt.v1" &&
    isRequestSubject(value.request) &&
    isSha256(value.requestHash) &&
    isSha256(value.scopeHash) &&
    isSha256(value.bundleContentHash) &&
    isSelection(value.selection) &&
    hasExactKeys(value.planner, ["identity", "receipt"]) &&
    hasExactKeys(value.planner.identity, ["role", "provider", "model"]) &&
    value.planner.identity.role === "knowledge-planner" &&
    value.planner.identity.provider === "vllm" &&
    isBoundedText(value.planner.identity.model, 512) &&
    isAgentReceipt(value.planner.receipt, "knowledge-planner") &&
    value.planner.identity.model === value.planner.receipt.servedModelId &&
    isSha256(value.contentHash) &&
    hasExactKeys(value.signature, ["algorithm", "keyId", "value"]) &&
    value.signature.algorithm === "hmac-sha256" &&
    isReceiptKeyId(value.signature.keyId) &&
    typeof value.signature.value === "string" &&
    value.signature.value.length >= 44 &&
    value.signature.value.length <= 88 &&
    /^[A-Za-z0-9+/]+={0,2}$/.test(value.signature.value)
  )
}

function isCriticReceipt(
  value: unknown,
): value is PlatformLibraryRagCriticReceiptV1 {
  return (
    hasExactKeys(value, [
      "schemaVersion",
      "request",
      "requestHash",
      "scopeHash",
      "bundleContentHash",
      "planReceiptContentHash",
      "compiledArtifactContentHash",
      "selection",
      "critic",
      "contentHash",
      "signature",
    ]) &&
    value.schemaVersion === "cogo.library-rag-critic-receipt.v1" &&
    isRequestSubject(value.request) &&
    isSha256(value.requestHash) &&
    isSha256(value.scopeHash) &&
    isSha256(value.bundleContentHash) &&
    isSha256(value.planReceiptContentHash) &&
    isSha256(value.compiledArtifactContentHash) &&
    isSelection(value.selection) &&
    hasExactKeys(value.critic, ["identity", "verdict", "receipt"]) &&
    hasExactKeys(value.critic.identity, ["role", "provider", "model"]) &&
    value.critic.identity.role === "semantic-critic" &&
    value.critic.identity.provider === "vllm" &&
    isBoundedText(value.critic.identity.model, 512) &&
    hasExactKeys(value.critic.verdict, [
      "schemaVersion",
      "verdict",
      "findings",
      "traceId",
    ]) &&
    value.critic.verdict.schemaVersion ===
      "cogo.library-semantic-critic-verdict.v1" &&
    value.critic.verdict.verdict === "pass" &&
    Array.isArray(value.critic.verdict.findings) &&
    value.critic.verdict.findings.length === 0 &&
    isTrace(value.critic.verdict.traceId) &&
    isAgentReceipt(value.critic.receipt, "semantic-critic") &&
    value.critic.identity.model === value.critic.receipt.servedModelId &&
    isSha256(value.contentHash) &&
    hasExactKeys(value.signature, ["algorithm", "keyId", "value"]) &&
    value.signature.algorithm === "hmac-sha256" &&
    isReceiptKeyId(value.signature.keyId) &&
    typeof value.signature.value === "string" &&
    value.signature.value.length >= 44 &&
    value.signature.value.length <= 88 &&
    /^[A-Za-z0-9+/]+={0,2}$/.test(value.signature.value)
  )
}

function hasExactKeys(
  value: unknown,
  keys: readonly string[],
): value is Record<string, unknown> {
  if (!isRecord(value)) return false
  const own = Object.keys(value)
  return own.length === keys.length && keys.every((key) => own.includes(key))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isLocale(value: unknown): value is PlatformLibraryLocale {
  return ["en-US", "ko-KR", "ja-JP", "zh-CN", "zh-TW"].includes(String(value))
}

function isBoundedText(value: unknown, max: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= max
}

function isClosedIdentifier(value: unknown): value is string {
  return typeof value === "string" && /^[a-z][A-Za-z0-9-]{0,159}$/.test(value)
}

function isAuthorityRef(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 1_024 && !/\s/.test(value)
}

function isSourceRef(value: unknown): value is string {
  return (
    isAuthorityRef(value) &&
    /^(?:cogo:|urn:cogo:|https:\/\/cogo\.xyz\/)/.test(value)
  )
}

function isSha256(value: unknown): value is `sha256:${string}` {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value)
}

function isVersion(value: unknown): value is string {
  return typeof value === "string" && /^[0-9]+\.[0-9]+\.[0-9]+$/.test(value)
}

function isTrace(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 256 && !/\s/.test(value)
}

function isReceiptKeyId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 128 &&
    /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/.test(value)
  )
}

function isTextArray(value: unknown, maxItems: number, maxLength: number): value is string[] {
  return Array.isArray(value) && value.length <= maxItems && value.every((item) => isBoundedText(item, maxLength))
}

function isUniqueRefs(value: unknown, maxItems: number): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.length <= maxItems &&
    value.every(isAuthorityRef) &&
    new Set(value).size === value.length
  )
}

function isUniqueSourceRefs(value: unknown, maxItems: number): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.length <= maxItems &&
    value.every(isSourceRef) &&
    new Set(value).size === value.length
  )
}

function sameOrderedValues(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index])
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  const a = [...new Set(left)].sort()
  const b = [...new Set(right)].sort()
  return a.length === b.length && a.every((item, index) => item === b[index])
}
